import { 
  OPERATION_DETAIL,
  LOGIN_STATUS,
  PATTERN_DATA_LIST,
  KEY_REPLACE_MAP,
  INNER_FIELD_ORDER,
  EXCLUDED_KEYS,
  DOCUMENT_CATEGORY_LIST,
  DOCUMENT_TYPE_LIST,
  FOLDER_DATA_LIST 
} from "../constants/Enums";


// ============================================================================
// 操作内容／ログイン状態の逆引きマップ
// ============================================================================
export const OperationByTiming = Object.fromEntries(
  Object.values(OPERATION_DETAIL).map(op => [op.id, op])
);

export const LoginStatusById = Object.fromEntries(
  Object.values(LOGIN_STATUS).map(st => [st.id, st])
);


// ============================================================================
// 操作内容の取得
// ============================================================================
export const getOperationDetail = (timing) =>
  OperationByTiming[Number(timing)] ?? null;


// ============================================================================
// ログイン状態の取得
// ============================================================================
export const getLoginStatus = (status) =>
  LoginStatusById[Number(status)] ?? null;


// ============================================================================
// PATTERN_MAP — department_id → pattern_name → pattern_item
// ============================================================================
export const PATTERN_MAP = PATTERN_DATA_LIST.reduce((acc, item) => {
  if (!acc[item.department_id]) acc[item.department_id] = {};
  acc[item.department_id][item.pattern_name] = item;
  return acc;
}, {});

export const getPattern = (department_id, pattern_name) =>
  PATTERN_MAP?.[department_id]?.[pattern_name] ?? null;


// ============================================================================
// PATTERN_TEXT_MAP_BY_NAME — pattern_name → pattern_text
// ============================================================================
export const PATTERN_TEXT_MAP_BY_NAME = PATTERN_DATA_LIST.reduce((acc, item) => {
  acc[item.pattern_name] = item.pattern_text;
  return acc;
}, {});


// ============================================================================
// 除外キー判定（*_norm も除外対象）
// ============================================================================
export const isExcludedKey = (key) => {
  if (EXCLUDED_KEYS.includes(key)) return true;
  if (key.endsWith("_norm")) return true;
  return false;
};


// ============================================================================
// 図面種類／図面タイプのマップ生成
// ============================================================================
export const DOCUMENT_CATEGORY_MAP = DOCUMENT_CATEGORY_LIST.reduce((acc, item) => {
  if (!acc[item.department_id]) acc[item.department_id] = {};
  acc[item.department_id][item.document_category] = item.document_category_text;
  return acc;
}, {});

export const getDocumentCategoryText = (dept, category) =>
  DOCUMENT_CATEGORY_MAP?.[dept]?.[category] ?? null;


export const DOCUMENT_TYPE_MAP = DOCUMENT_TYPE_LIST.reduce((acc, item) => {
  if (!acc[item.department_id]) acc[item.department_id] = {};
  acc[item.department_id][item.document_type] = item.document_type_text;
  return acc;
}, {});

export const getDocumentTypeText = (dept, type) =>
  DOCUMENT_TYPE_MAP?.[dept]?.[type] ?? null;

// ============================================================================
// FOLDER_EDITION_MAP
//   folder_id → folder_edition 変換マップ
// ============================================================================
const FOLDER_EDITION_MAP = FOLDER_DATA_LIST.reduce((acc, item) => {
  acc[item.folder_id] = item.folder_edition;
  return acc;
}, {});

export const getFolderEdition = (folderId) =>
  FOLDER_EDITION_MAP?.[folderId] ?? null;


// ============================================================================
// normalizeContent — content を画面表示用に整形
// ============================================================================
export const normalizeContent = (content) => {
  if (!content || typeof content !== "object") return {};

  const result = {};

  Object.entries(content).forEach(([key, val]) => {

    // ----------------------------------------------------------
    // 除外キー → 完全に無視
    // ----------------------------------------------------------
    if (isExcludedKey(key)) return;

    // ----------------------------------------------------------
    // pattern_name → pattern_text を優先的に使用
    // ----------------------------------------------------------
    let displayKey = PATTERN_TEXT_MAP_BY_NAME[key] ?? key;

    // KEY_REPLACE_MAP に label 定義があれば上書き
    const cfg = KEY_REPLACE_MAP[key];
    if (cfg?.label) displayKey = cfg.label;


    // ----------------------------------------------------------
    // convert(content, value) を持つキー（document_category / document_type）
    // ----------------------------------------------------------
    if (cfg?.convert) {
      const jpText = cfg.convert(content, val);
      if (jpText) result[displayKey] = jpText;
      return;
    }


    // ----------------------------------------------------------
    // valueMap を持つプリミティブ項目（例：and_flag）
    // ----------------------------------------------------------
    if (cfg?.valueMap && typeof val !== "object") {
      const jpValue = cfg.valueMap[val];
      if (jpValue !== undefined) result[displayKey] = jpValue;
      return;
    }


    // ----------------------------------------------------------
    // nested object（value / option / ambiguous）を含む項目の処理
    // ----------------------------------------------------------
    const isNestedObject = val && typeof val === "object" && "value" in val;

    if (isNestedObject) {

      // 空文字は削除
      if (val.value === "") return;

      const temp = {};

      Object.entries(val).forEach(([innerKey, innerVal]) => {
        const innerCfg = KEY_REPLACE_MAP[innerKey];
        if (!innerCfg) return;

        const jpKey = innerCfg.label ?? innerKey;

        temp[jpKey] =
          innerCfg.valueMap && innerCfg.valueMap[innerVal] !== undefined
            ? innerCfg.valueMap[innerVal]
            : innerVal;
      });

      // 並び順調整（値 → オプション → あいまい）
      const ordered = {};
      INNER_FIELD_ORDER.forEach((jpKey) => {
        if (temp[jpKey] !== undefined) ordered[jpKey] = temp[jpKey];
      });

      // 残りを末尾へ
      Object.entries(temp).forEach(([k, v]) => {
        if (!(k in ordered)) ordered[k] = v;
      });

      result[displayKey] = ordered;
      return;
    }


    // ----------------------------------------------------------
    // 通常のプリミティブ値
    // ----------------------------------------------------------
    if (val !== "" && val !== null && val !== undefined) {
      result[displayKey] = val;
    }
  });

  return result;
};